import React, { useState } from 'react'
import { Argument as ClassName } from 'classnames'
import styled from 'styled-components'

import canActivateFirmware from '../canActivateFirmware'
import displayDateTime from '../displayDateTime'
import Text from '../../ui/Text'
import { FirmwareGeneralModel } from '../../common/redux/api/web-api'
import { EditFirmwareModal } from '../../support/components/EditFirmwareModal'
import { EditIcon } from '../../common/icons/edit-icon'
import TrashIcon from '../icons/trash-icon/trash-icon'
import { TableBody, TableBodyCell, TableRow } from '../table'
import DropdownMenu, { DropdownMenuItem } from '../../common/components/DropdownMenu'
import { Button } from '../../common/components/Button'
import { CheckMarkIcon } from '../../common/icons/checkmark-icon'
import useText from '../../common/hooks/useText'

export const STextarea = styled.textarea`
    font-weight: 400;
    display: block;
    width: 100%;
    height: auto;
    padding: 6px 12px;
    font-size: 14px;
    line-height: 1.42857143;
    color: #555;
    background-color: #fff;
    background-image: none;
    border: 1px solid #ccc;
    border-radius: 4px;
`

const SActiveFirmwareMessage = styled.div`
    font-weight: 500;
    padding-top: 10px;
`

export type FirmwareListItemProps = {
    data: FirmwareGeneralModel
    onActivate?: (id: number) => void
    onRemove?: (id: number) => void
    className?: ClassName
    style?: React.CSSProperties
    isOcpp?: boolean
    isActivationDisabled?: boolean
    showActionMenu?: boolean
}

export function FirmwareListItem({
    isOcpp,
    data,
    onActivate,
    onRemove,
    isActivationDisabled,
    showActionMenu,
}: FirmwareListItemProps): React.ReactElement | null {
    const text = useText()
    const canSelectFirmware = canActivateFirmware(data.firmwareType) && !data.isActive && onActivate && !isOcpp
    const [editFirmwareModalIsOpen, setEditFirmwareModalIsOpen] = useState(false)

    const [selectedFirmwareData, setSelectedFirmwareData] = useState<FirmwareGeneralModel | null>(null)
    const openModal = (selectedData: FirmwareGeneralModel) => {
        setSelectedFirmwareData(selectedData)
        setEditFirmwareModalIsOpen(true)
    }

    return (
        <>
            {data && (
                <TableBody>
                    <TableRow>
                        <TableBodyCell data-testid="list-fw-version">{data.firmwareVersion}</TableBodyCell>
                        <TableBodyCell data-testid="list-upload-date">{displayDateTime(data.uploadDate)}</TableBodyCell>
                        <TableBodyCell data-testid="list-status-">{data.firmwareStatus}</TableBodyCell>
                        <TableBodyCell data-testid="list-desc">
                            {data.description}
                            {data.isActive && data.productId && (
                                <SActiveFirmwareMessage data-testid="list-active">
                                    <Text
                                        code="msgFWActivateFWMsg"
                                        values={{ type: data.productId > 3 ? 'Aura' : 'Halo' }}
                                    />
                                </SActiveFirmwareMessage>
                            )}
                        </TableBodyCell>
                        {isOcpp && (
                            <TableBodyCell data-testid="list-ocpp-enabled">
                                {data.ocppEnabledFirmware && <CheckMarkIcon />}
                            </TableBodyCell>
                        )}
                        <TableBodyCell data-testid="list-count">{data.chargePointsCount}</TableBodyCell>
                        <TableBodyCell data-testid="list-count">
                            {canSelectFirmware && (
                                <Button
                                    size="sm"
                                    label={text('lblUpgradeFirmware')}
                                    disabled={isActivationDisabled}
                                    type="button"
                                    onClick={() => onActivate(data.id)}
                                />
                            )}
                        </TableBodyCell>
                        {showActionMenu && (
                            <TableBodyCell>
                                <DropdownMenu>
                                    <DropdownMenuItem
                                        text={text('lblEdit')}
                                        icon={<EditIcon size={14} />}
                                        onClick={() => {
                                            openModal(data)
                                            setEditFirmwareModalIsOpen(true)
                                        }}
                                    ></DropdownMenuItem>

                                    <DropdownMenuItem
                                        text={text('btnRemove')}
                                        icon={<TrashIcon size={14} />}
                                        disabled={data.isActive || !!data.chargePointsCount}
                                        onClick={() => onRemove && onRemove(data?.id)}
                                    ></DropdownMenuItem>
                                </DropdownMenu>
                            </TableBodyCell>
                        )}
                    </TableRow>
                </TableBody>
            )}

            {editFirmwareModalIsOpen && (
                <EditFirmwareModal
                    setEditFirmwareModalIsOpen={setEditFirmwareModalIsOpen}
                    selectedFirmwareData={selectedFirmwareData}
                />
            )}
        </>
    )
}
